/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { useCallback, useRef } from 'react'

import { clampFraction } from './layout'
import type { MarkerRef, Position } from './types'

interface DragHandlers {
  onPointerDown: (e: React.PointerEvent<HTMLElement>) => void
  onPointerMove: (e: React.PointerEvent<HTMLElement>) => void
  onPointerUp: (e: React.PointerEvent<HTMLElement>) => void
}

/**
 * Free drag of a marker inside its surface, in edit mode. Pointer capture keeps the
 * drag alive when the cursor outruns the icon; every move reports the pointer's place
 * as fractions of the surface, clamped to it, which is what the API stores. With a
 * grid pitch the place is rounded to the nearest grid point first, in the surface's
 * rendered pixels, so the grid the user sees is the grid the markers land on.
 */

/** The nearest multiple of `pitch`, or the value itself with no pitch. */
export const snap = (px: number, pitch: number): number => (pitch > 0 ? Math.round(px / pitch) * pitch : px)
export const useDragMarkers = (
  surfaceRef: React.RefObject<HTMLElement | null>,
  enabled: boolean,
  onMove: (marker: MarkerRef, position: Position) => void,
  /** Grid pitch in surface pixels to snap to; 0 or undefined for free placement. */
  snapPx = 0
) => {
  const dragging = useRef<MarkerRef | null>(null)

  const bind = useCallback(
    (marker: MarkerRef): DragHandlers => ({
      onPointerDown: (e) => {
        if (!enabled) return
        e.preventDefault()
        e.stopPropagation()
        e.currentTarget.setPointerCapture?.(e.pointerId)
        dragging.current = marker
      },
      onPointerMove: (e) => {
        const surface = surfaceRef.current
        if (!enabled || !dragging.current || !surface) return
        const rect = surface.getBoundingClientRect()
        if (rect.width === 0 || rect.height === 0) return
        onMove(dragging.current, {
          x: clampFraction(snap(e.clientX - rect.left, snapPx) / rect.width),
          y: clampFraction(snap(e.clientY - rect.top, snapPx) / rect.height),
        })
      },
      onPointerUp: (e) => {
        if (!dragging.current) return
        dragging.current = null
        if (e.currentTarget.hasPointerCapture?.(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId)
      },
    }),
    [surfaceRef, enabled, onMove, snapPx]
  )

  return { bind }
}
